import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Class,
  Course,
  DAY_OF_WEEK_TO_NAME,
  School,
  Semester,
  Student,
  Teacher,
} from "../models";
import { logger } from "../utils/logger";

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
  school?: {
    id: string;
    name: string;
    isVerified: boolean;
  };
}

class ClassController {
  /**
   * Helper: Resolve schoolId from request, with fallback to user lookup
   */
  private async resolveSchoolId(req: AuthRequest): Promise<string | null> {
    // Prefer explicit school context if attached by middleware
    let schoolId = (req as any).school?.id || req.user?.schoolId;

    // If schoolId is missing, try to resolve it from the authenticated user (school users)
    if (!schoolId) {
      try {
        const userId = req.user?.userId;
        if (userId && mongoose.Types.ObjectId.isValid(userId)) {
          const foundSchool = await School.findOne({
            userId: mongoose.Types.ObjectId.createFromHexString(userId),
          }).select("_id schoolName isVerified");
          if (foundSchool) {
            schoolId = foundSchool._id.toString();
            // attach to request for downstream use
            (req as any).school = {
              id: schoolId,
              name: foundSchool.schoolName,
              isVerified: foundSchool.isVerified,
            };
          }
        }
      } catch (error) {
        logger.warn("Failed to auto-resolve schoolId from userId:", error);
      }
    }

    return schoolId || null;
  }

  /**
   * Create a new class
   * @route POST /api/v1/school/classes
   * @access Private (School Admin)
   */
  // TODO: Add validation for the request body
  async createClass(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        semesterId, // Required
        courseId, // Optional: links to the Course catalog
        className: classNameInput, // Optional: explicit class name from FE
        courseName, // Optional: course display name
        courseCode, // Optional: e.g. "CS101"
        credits, // Optional
        description, // Optional
        subject: subjectInput, // Optional
        sectionCode, // Optional now (auto-fallback to "A" or derived)
        title, // Optional full display name
        teacherId, // Optional: single teacher
        teacherIds: teacherIdsInput, // Optional: multi-teacher array
        studentIds = [], // Optional
        maxStudents, // Optional
        // Schedule (either form is accepted)
        schedule: scheduleInput, // Optional: array form [{ day, startTime, endTime }]
        dayOfWeek, // Optional: flat form (1-7 or array)
        startTime, // Optional: flat form
        endTime, // Optional: flat form
        // Attendance windows
        checkInStartTime, // Optional
        checkInEndTime, // Optional
        restroomStartTime, // Optional
        restroomEndTime, // Optional
        restRoomMinutes, // Optional
        // Room/location
        roomNumber, // Optional
        classRoom, // Optional (alt of roomNumber)
        classRoomNumber, // Optional
        building, // Optional
        floor, // Optional
        location, // Optional
        materials = [], // Optional
        isActive = true,
      } = req.body;

      const userId = req.user?.userId;
      const schoolId = await this.resolveSchoolId(req);

      if (!schoolId || !userId) {
        res.status(400).json({
          success: false,
          message: "School ID and User ID are required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!semesterId) {
        res.status(400).json({
          success: false,
          message: "Semester ID is required",
          error: "Missing required fields",
        } as APIResponse);
        return;
      }

      // Resolve teacher list: prefer teacherIds[], else [teacherId], else []
      let rawTeacherIds: string[] = [];
      if (Array.isArray(teacherIdsInput)) {
        rawTeacherIds = teacherIdsInput;
      } else if (teacherId) {
        rawTeacherIds = [teacherId];
      }

      if (rawTeacherIds.length === 0) {
        res.status(400).json({
          success: false,
          message:
            "At least one teacher (teacherId or teacherIds[]) is required",
          error: "Missing required fields",
        } as APIResponse);
        return;
      }

      // Resolve schedule: prefer explicit schedule[]; else build from dayOfWeek+startTime+endTime
      let schedule: {
        day: string;
        startTime: string;
        endTime: string;
        isActive: boolean;
      }[] = [];

      if (Array.isArray(scheduleInput) && scheduleInput.length > 0) {
        schedule = scheduleInput.map((s: any) => ({
          day: String(s.day).toLowerCase(),
          startTime:
            s.startTime?.length >= 5 ? s.startTime.slice(0, 5) : s.startTime,
          endTime: s.endTime?.length >= 5 ? s.endTime.slice(0, 5) : s.endTime,
          isActive: s.isActive ?? true,
        }));
      } else if (dayOfWeek && startTime && endTime) {
        const days = Array.isArray(dayOfWeek) ? dayOfWeek : [dayOfWeek];
        schedule = days.map((d: number) => ({
          day: DAY_OF_WEEK_TO_NAME[d] || "monday",
          startTime: startTime.length >= 5 ? startTime.slice(0, 5) : startTime,
          endTime: endTime.length >= 5 ? endTime.slice(0, 5) : endTime,
          isActive: true,
        }));
      } else {
        res.status(400).json({
          success: false,
          message:
            "Provide schedule[] (array of { day, startTime, endTime }) OR dayOfWeek + startTime + endTime",
          error: "Missing schedule",
        } as APIResponse);
        return;
      }

      // Validate semester exists and belongs to school
      const semester = await Semester.findOne({
        _id: mongoose.Types.ObjectId.createFromHexString(semesterId),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
      });

      if (!semester) {
        res.status(400).json({
          success: false,
          message:
            "Invalid semester ID or semester doesn't belong to your school",
          error: "Invalid semester",
        } as APIResponse);
        return;
      }

      // If courseId is provided, validate course exists and belongs to school/semester.
      // Otherwise fall back to inline course fields from the request body.
      let resolvedCourseName: string | undefined;
      let resolvedCourseCode: string | undefined;
      let resolvedCredits: number | undefined;
      let resolvedDescription: string | undefined;
      let resolvedMaxStudents: number | undefined;

      if (courseId) {
        const course = await Course.findOne({
          _id: mongoose.Types.ObjectId.createFromHexString(courseId),
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
          semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId),
        });

        if (!course) {
          res.status(400).json({
            success: false,
            message:
              "Invalid course ID or course doesn't belong to this semester",
            error: "Invalid course",
          } as APIResponse);
          return;
        }

        resolvedCourseName = course.courseName;
        resolvedCourseCode = course.courseCode;
        resolvedCredits = course.credits;
        resolvedDescription = course.description;
        resolvedMaxStudents = (course as any).maxStudents;
      } else {
        // Use inline fields. Fall back to className if courseName is missing.
        resolvedCourseName =
          (courseName as string)?.trim() ||
          (classNameInput as string)?.trim() ||
          undefined;
        resolvedCourseCode = (courseCode as string)?.trim()?.toUpperCase();
        resolvedCredits = credits;
        resolvedDescription = (description as string)?.trim();
      }

      // Validate teachers belong to school and are approved
      const teacherObjectIds = rawTeacherIds.map((id: string) =>
        mongoose.Types.ObjectId.createFromHexString(id),
      );
      const teachers = await Teacher.find({
        _id: { $in: teacherObjectIds },
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        isApproved: true,
      });

      if (teachers.length !== rawTeacherIds.length) {
        res.status(400).json({
          success: false,
          message:
            "One or more teachers are invalid, not approved, or don't belong to your school",
          error: "Invalid teacher",
        } as APIResponse);
        return;
      }

      // Validate students if provided
      let validatedStudentIds: mongoose.Types.ObjectId[] = [];
      const studentIdsArray = Array.isArray(studentIds) ? studentIds : [];
      if (studentIdsArray.length > 0) {
        const students = await Student.find({
          _id: { $in: studentIdsArray },
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
          isApproved: true,
        });

        if (students.length !== studentIdsArray.length) {
          res.status(400).json({
            success: false,
            message: "Some students are invalid or not approved",
            error: "Invalid students",
          } as APIResponse);
          return;
        }

        validatedStudentIds = studentIdsArray.map((id: string) =>
          mongoose.Types.ObjectId.createFromHexString(id),
        );
      }

      // Derive className:
      //   1. explicit className from FE
      //   2. else `title`
      //   3. else `<courseName|courseCode> - <sectionCode>`
      //   4. else "Class"
      const className =
        (classNameInput as string)?.trim() ||
        (title as string)?.trim() ||
        (resolvedCourseName || resolvedCourseCode
          ? `${resolvedCourseName || resolvedCourseCode}${sectionCode ? ` - ${sectionCode}` : ""}`.trim()
          : "Class");

      // Derive classCode: courseCode-SectionCode (only if we have a courseCode;
      // otherwise let the model's pre-save hook auto-generate one like C0001)
      let classCode: string | undefined;
      if (resolvedCourseCode && sectionCode) {
        classCode = `${resolvedCourseCode}-${sectionCode}`.toUpperCase();
      } else if (resolvedCourseCode) {
        classCode = resolvedCourseCode.toUpperCase();
      }

      // Map materials if provided (support both old and new format)
      const mappedMaterials = Array.isArray(materials)
        ? materials.map((m: any) => ({
            fileName: m.fileName || m.fileUrl?.split("/").pop() || "material",
            originalName: m.originalName || m.title,
            fileSize: m.fileSize || 0,
            mimeType: m.mimeType,
            fileUrl: m.fileUrl,
            uploadedAt: m.uploadedAt ? new Date(m.uploadedAt) : new Date(),
            uploadedBy: userId
              ? mongoose.Types.ObjectId.createFromHexString(userId)
              : undefined,
          }))
        : [];

      // Create class
      const newClass = new Class({
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        semesterId: mongoose.Types.ObjectId.createFromHexString(semesterId),
        courseId: courseId
          ? mongoose.Types.ObjectId.createFromHexString(courseId)
          : undefined,
        sectionCode: sectionCode?.trim(),
        title: title?.trim(),
        className,
        classCode,
        courseCode: resolvedCourseCode,
        courseName: resolvedCourseName,
        credits: resolvedCredits,
        description: resolvedDescription,
        subject: subjectInput?.trim() || resolvedCourseName,
        teacherIds: teacherObjectIds,
        studentIds: validatedStudentIds,
        maxStudents: maxStudents ?? resolvedMaxStudents,
        currentStudents: validatedStudentIds.length,
        checkInStartTime,
        checkInEndTime,
        restroomStartTime,
        restroomEndTime,
        restRoomMinutes,
        roomNumber: roomNumber || classRoomNumber,
        classRoom,
        classRoomNumber: classRoomNumber || roomNumber,
        building,
        floor,
        location,
        schedule,
        settings: checkInEndTime
          ? { finalAttendanceTime: checkInEndTime }
          : undefined,
        materials: mappedMaterials.length > 0 ? mappedMaterials : undefined,
        isActive,
        createdBy: mongoose.Types.ObjectId.createFromHexString(userId),
      });

      await newClass.save();

      logger.info(`Class created: ${newClass.id}`, {
        className,
        courseId,
        sectionCode,
        semesterId,
        schoolId,
        userId,
      });

      res.status(201).json({
        success: true,
        message: "Class created successfully",
        data: {
          class: {
            id: newClass.id,
            courseId: newClass.courseId,
            sectionCode: newClass.sectionCode,
            title: newClass.title,
            className: newClass.className,
            classCode: newClass.classCode,
            courseCode: newClass.courseCode,
            courseName: newClass.courseName,
            credits: newClass.credits,
            description: newClass.description,
            subject: newClass.subject,
            status: newClass.status,
            isActive: newClass.isActive,
            maxStudents: newClass.maxStudents,
            currentStudents: newClass.currentStudents,
            roomNumber: newClass.roomNumber,
            building: newClass.building,
            floor: newClass.floor,
            location: newClass.location,
            checkInStartTime: newClass.checkInStartTime,
            checkInEndTime: newClass.checkInEndTime,
            restroomStartTime: newClass.restroomStartTime,
            restroomEndTime: newClass.restroomEndTime,
            schedule: newClass.schedule,
            semesterId: newClass.semesterId,
            teacherIds: newClass.teacherIds,
            studentIds: newClass.studentIds,
            materials: newClass.materials,
            createdAt: newClass.createdAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error creating class:", error);

      // Handle duplicate class name
      if (error.code === 11_000) {
        res.status(409).json({
          success: false,
          message: "A class with this name already exists in your school",
          error: "Duplicate class name",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to create class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all classes for a school or semester
   * @route GET /api/v1/school/classes?semesterId=:semesterId
   * @access Private (School Admin)
   */
  async getAllClasses(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { semesterId } = req.query;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      let classes;
      // eslint-disable-next-line unicorn/prefer-ternary
      if (semesterId) {
        // Get classes for specific semester
        classes = await Class.findBySemester(
          mongoose.Types.ObjectId.createFromHexString(semesterId as string),
        ).populate("semester", "semesterName academicYear startDate endDate");
      } else {
        // Get all classes for school
        classes = await Class.findBySchool(
          mongoose.Types.ObjectId.createFromHexString(schoolId),
        ).populate("semester", "semesterName academicYear startDate endDate");
      }

      res.status(200).json({
        success: true,
        message: `${classes.length} classes found`,
        data: {
          classes: classes.map(classItem => ({
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            courseCode: classItem.courseCode,
            courseName: classItem.courseName,
            credits: classItem.credits,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.currentStudents,
            availableSpots: classItem.availableSpots,
            enrollmentPercentage: classItem.enrollmentPercentage,
            classRoom: classItem.classRoom,
            schedule: classItem.schedule,
            semester: (classItem as any).semester,
            createdAt: classItem.createdAt,
          })),
          totalCount: classes.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching classes:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch classes",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get class by ID with full details
   * @route GET /api/v1/school/classes/:id
   * @access Private (School Admin)
   */
  async getClassById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      // Prefer explicit school context if attached by middleware, fallback to user.schoolId
      let schoolId = (req as any).school?.id || req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // If schoolId is missing, try to resolve it from the authenticated user (school users)
      if (!schoolId) {
        try {
          const userId = req.user?.userId;
          if (userId && mongoose.Types.ObjectId.isValid(userId)) {
            const foundSchool = await School.findOne({
              userId: mongoose.Types.ObjectId.createFromHexString(userId),
            }).select("_id schoolName isVerified");
            if (foundSchool) {
              schoolId = foundSchool._id.toString();
              // attach to request for downstream use
              (req as any).school = {
                id: schoolId,
                name: foundSchool.schoolName,
                isVerified: foundSchool.isVerified,
              };
            }
          }
        } catch (error) {
          logger.warn("Failed to auto-resolve schoolId from userId:", error);
        }
      }

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Build query filter for schoolId
      const queryFilter: any = { _id: id };
      queryFilter.schoolId = mongoose.Types.ObjectId.isValid(schoolId)
        ? mongoose.Types.ObjectId.createFromHexString(schoolId)
        : schoolId;

      const classItem = await Class.findOne(queryFilter)
        .populate(
          "teacherIds",
          "fullName employeeId profilePicture phone email subjects",
        )
        .populate(
          "studentIds",
          "fullName studentId profilePicture phone academicLevel academicYear status approvalStatus isApproved userId",
        )
        .populate("semesterId", "semesterName academicYear startDate endDate");

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      // Format students to include both Student._id (id) and User._id (userId)
      const populatedStudents = ((classItem as any).studentIds || []) as any[];
      const formattedStudents = populatedStudents.map(s => ({
        id: s._id,
        userId: s.userId?._id ?? s.userId,
        studentId: s.studentId,
        fullName: s.fullName,
        phone: s.phone,
        academicLevel: s.academicLevel,
        academicYear: s.academicYear,
        status: s.status,
        approvalStatus: s.approvalStatus,
        isApproved: s.isApproved,
        profilePicture: s.profilePicture,
      }));

      res.status(200).json({
        success: true,
        message: "Class found",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.currentStudents,
            availableSpots: classItem.availableSpots,
            enrollmentPercentage: classItem.enrollmentPercentage,
            classRoom: classItem.classRoom,
            schedule: classItem.schedule,
            settings: classItem.settings,
            materials: classItem.materials,
            semester: (classItem as any).semesterId,
            teachers: (classItem as any).teacherIds,
            students: formattedStudents,
            studentCount: formattedStudents.length,
            createdAt: classItem.createdAt,
            updatedAt: classItem.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching class:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update class
   * @route PUT /api/v1/school/classes/:id
   * @access Private (School Admin)
   */
  async updateClass(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const {
        semesterId,
        courseId,
        sectionCode,
        className,
        courseName,
        courseCode,
        credits,
        title,
        description,
        teacherId,
        teacherIds,
        studentIds: studentIdsInput,
        maxStudents,
        subject,
        classRoom,
        classRoomNumber,
        roomNumber,
        building,
        floor,
        location,
        schedule,
        dayOfWeek,
        startTime,
        endTime,
        checkInStartTime,
        checkInEndTime,
        restroomStartTime,
        restroomEndTime,
        restRoomMinutes,
        settings,
        status,
        isActive,
      } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      }).populate("courseId", "courseCode courseName credits description");

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      // If semesterId is being updated, validate it belongs to this school
      if (semesterId !== undefined) {
        const semester = await Semester.findOne({
          _id: mongoose.Types.ObjectId.createFromHexString(semesterId),
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
        });
        if (!semester) {
          res.status(400).json({
            success: false,
            message:
              "Invalid semester ID or semester doesn't belong to your school",
            error: "Invalid semester",
          } as APIResponse);
          return;
        }
        classItem.semesterId =
          mongoose.Types.ObjectId.createFromHexString(semesterId);
      }

      // Inline course-field updates (when not using courseId catalog lookup)
      if (courseId === undefined) {
        if (courseName !== undefined) {
          classItem.courseName = courseName?.trim();
          classItem.subject = subject?.trim() || courseName?.trim();
        }
        if (courseCode !== undefined) {
          const upper = courseCode?.trim()?.toUpperCase();
          classItem.courseCode = upper;
          // Recompute classCode if we have section
          const sec = sectionCode ?? classItem.sectionCode;
          if (upper) {
            classItem.classCode = sec ? `${upper}-${sec}`.toUpperCase() : upper;
          }
        }
        if (credits !== undefined) classItem.credits = credits;
      }

      // If courseId is being updated, validate and re-populate from Course
      if (courseId !== undefined) {
        const course = await Course.findOne({
          _id: mongoose.Types.ObjectId.createFromHexString(courseId),
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
          semesterId: classItem.semesterId,
        });

        if (!course) {
          res.status(400).json({
            success: false,
            message:
              "Invalid course ID or course doesn't belong to this semester",
            error: "Invalid course",
          } as APIResponse);
          return;
        }

        classItem.courseId =
          mongoose.Types.ObjectId.createFromHexString(courseId);
        classItem.courseCode = course.courseCode;
        classItem.courseName = course.courseName;
        classItem.credits = course.credits;
        classItem.description = course.description ?? classItem.description;
        classItem.subject = course.courseName;

        if (sectionCode !== undefined) {
          classItem.sectionCode = sectionCode.trim();
          classItem.classCode =
            `${course.courseCode}-${sectionCode}`.toUpperCase();
        } else if (classItem.sectionCode) {
          classItem.classCode =
            `${course.courseCode}-${classItem.sectionCode}`.toUpperCase();
        }

        const displayName =
          title?.trim() ||
          className ||
          `${course.courseName || course.courseCode} - ${classItem.sectionCode || "?"}`.trim();
        classItem.className = displayName;
        if (title !== undefined) classItem.title = title?.trim();
      } else if (sectionCode !== undefined && classItem.courseId) {
        const courseCode =
          classItem.courseCode || (classItem as any).courseId?.courseCode;
        if (courseCode) {
          classItem.sectionCode = sectionCode.trim();
          classItem.classCode = `${courseCode}-${sectionCode}`.toUpperCase();
        }
      }

      // Validate teachers if being updated (support both single and multiple)
      if (teacherId !== undefined || teacherIds !== undefined) {
        const teacherIdsToValidate =
          teacherIds || (teacherId ? [teacherId] : []);

        if (teacherIdsToValidate.length > 0) {
          const teachers = await Teacher.find({
            _id: { $in: teacherIdsToValidate },
            schoolId: mongoose.Types.ObjectId.createFromHexString(
              schoolId || "",
            ),
            isApproved: true,
          });

          if (teachers.length !== teacherIdsToValidate.length) {
            res.status(400).json({
              success: false,
              message: "Some teachers are invalid or not approved",
              error: "Invalid teachers",
            } as APIResponse);
            return;
          }

          classItem.teacherIds = teacherIdsToValidate.map((id: string) =>
            mongoose.Types.ObjectId.createFromHexString(id),
          );
        } else {
          classItem.teacherIds = [];
        }
      }

      // Full-replacement studentIds: replace the enrolled students with the
      // provided list (validated). Mutually exclusive with addStudents/removeStudents.
      if (Array.isArray(studentIdsInput)) {
        const studentObjectIds = studentIdsInput.map((sid: string) =>
          mongoose.Types.ObjectId.createFromHexString(sid),
        );
        if (studentObjectIds.length > 0) {
          const studentsExist = await Student.find({
            _id: { $in: studentObjectIds },
            schoolId: mongoose.Types.ObjectId.createFromHexString(
              schoolId || "",
            ),
            isApproved: true,
          });
          if (studentsExist.length !== studentObjectIds.length) {
            res.status(400).json({
              success: false,
              message: "Some students are invalid or not approved",
              error: "Invalid students",
            } as APIResponse);
            return;
          }
        }
        const cap = maxStudents ?? classItem.maxStudents;
        if (cap && studentObjectIds.length > cap) {
          res.status(400).json({
            success: false,
            message: `Cannot enroll ${studentObjectIds.length} students. Capacity is ${cap}.`,
            error: "Capacity exceeded",
          } as APIResponse);
          return;
        }
        classItem.studentIds = studentObjectIds;
        classItem.currentStudents = studentObjectIds.length;
      }

      // Handle add/remove students
      if (req.body.addStudents && Array.isArray(req.body.addStudents)) {
        const addStudentIds = req.body.addStudents.map((id: string) =>
          mongoose.Types.ObjectId.createFromHexString(id),
        );
        // Validate students
        const studentsToAdd = await Student.find({
          _id: { $in: addStudentIds },
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
          isApproved: true,
        });
        if (studentsToAdd.length !== addStudentIds.length) {
          res.status(400).json({
            success: false,
            message: "Some students to add are invalid or not approved",
            error: "Invalid students",
          } as APIResponse);
          return;
        }
        await classItem.enrollStudents(addStudentIds);
      }

      if (req.body.removeStudents && Array.isArray(req.body.removeStudents)) {
        for (const studentId of req.body.removeStudents) {
          await classItem.unenrollStudent(
            mongoose.Types.ObjectId.createFromHexString(studentId),
          );
        }
      }

      // Handle add/remove teachers
      if (req.body.addTeachers && Array.isArray(req.body.addTeachers)) {
        const addTeacherIds = req.body.addTeachers.map((id: string) =>
          mongoose.Types.ObjectId.createFromHexString(id),
        );
        // Validate teachers
        const teachersToAdd = await Teacher.find({
          _id: { $in: addTeacherIds },
          schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
          isApproved: true,
        });
        if (teachersToAdd.length !== addTeacherIds.length) {
          res.status(400).json({
            success: false,
            message: "Some teachers to add are invalid or not approved",
            error: "Invalid teachers",
          } as APIResponse);
          return;
        }
        await classItem.assignTeachers(addTeacherIds);
      }

      if (req.body.removeTeachers && Array.isArray(req.body.removeTeachers)) {
        for (const teacherId of req.body.removeTeachers) {
          await classItem.unassignTeacher(
            mongoose.Types.ObjectId.createFromHexString(teacherId),
          );
        }
      }

      // Update fields if provided (only if not already set by courseId logic)
      if (courseId === undefined) {
        if (className !== undefined) classItem.className = className;
        if (title !== undefined) classItem.title = title?.trim();
      }
      if (description !== undefined) classItem.description = description;
      if (maxStudents !== undefined) classItem.maxStudents = maxStudents;
      if (subject !== undefined) classItem.subject = subject;
      if (classRoom !== undefined) classItem.classRoom = classRoom;
      if (roomNumber !== undefined) {
        classItem.roomNumber = roomNumber;
        classItem.classRoomNumber = roomNumber;
      }
      if (classRoomNumber !== undefined) {
        classItem.classRoomNumber = classRoomNumber;
        if (roomNumber === undefined) classItem.roomNumber = classRoomNumber;
      }
      if (building !== undefined) classItem.building = building;
      if (floor !== undefined) classItem.floor = floor;
      if (location !== undefined) classItem.location = location;
      if (Array.isArray(schedule)) {
        classItem.schedule = schedule.map((s: any) => ({
          day: String(s.day).toLowerCase(),
          startTime:
            s.startTime?.length >= 5 ? s.startTime.slice(0, 5) : s.startTime,
          endTime: s.endTime?.length >= 5 ? s.endTime.slice(0, 5) : s.endTime,
          isActive: s.isActive ?? true,
        }));
      } else if (dayOfWeek !== undefined && startTime && endTime) {
        const days = Array.isArray(dayOfWeek) ? dayOfWeek : [dayOfWeek];
        classItem.schedule = days.map((d: number) => ({
          day: DAY_OF_WEEK_TO_NAME[d] || "monday",
          startTime: startTime.length >= 5 ? startTime.slice(0, 5) : startTime,
          endTime: endTime.length >= 5 ? endTime.slice(0, 5) : endTime,
          isActive: true,
        }));
      }
      if (restRoomMinutes !== undefined)
        classItem.restRoomMinutes = restRoomMinutes;
      if (checkInStartTime !== undefined)
        classItem.checkInStartTime = checkInStartTime;
      if (checkInEndTime !== undefined) {
        classItem.checkInEndTime = checkInEndTime;
        if (classItem.settings) {
          classItem.settings = {
            ...classItem.settings,
            finalAttendanceTime: checkInEndTime,
          };
        }
      }
      if (restroomStartTime !== undefined)
        classItem.restroomStartTime = restroomStartTime;
      if (restroomEndTime !== undefined)
        classItem.restroomEndTime = restroomEndTime;
      if (settings !== undefined)
        classItem.settings = { ...classItem.settings, ...settings };
      if (status !== undefined) classItem.status = status;
      if (isActive !== undefined) classItem.isActive = isActive;

      await classItem.save();

      logger.info(`Class updated: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Class updated successfully",
        data: {
          class: {
            id: classItem.id,
            courseId: classItem.courseId,
            sectionCode: classItem.sectionCode,
            title: classItem.title,
            className: classItem.className,
            classCode: classItem.classCode,
            courseCode: classItem.courseCode,
            courseName: classItem.courseName,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.currentStudents,
            roomNumber: classItem.roomNumber,
            building: classItem.building,
            floor: classItem.floor,
            location: classItem.location,
            checkInStartTime: classItem.checkInStartTime,
            checkInEndTime: classItem.checkInEndTime,
            schedule: classItem.schedule,
            teacherIds: classItem.teacherIds,
            updatedAt: classItem.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error updating class:", error);

      // Handle duplicate class name
      if (error.code === 11_000) {
        res.status(409).json({
          success: false,
          message: "A class with this name already exists in your school",
          error: "Duplicate class name",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to update class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Delete class
   * @route DELETE /api/v1/school/classes/:id
   * @access Private (School Admin)
   */
  async deleteClass(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOneAndDelete({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      logger.info(`Class deleted: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Class deleted successfully",
        data: {
          deletedClass: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error deleting class:", error);
      res.status(500).json({
        success: false,
        message: "Failed to delete class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Assign teacher to class
   * @route POST /api/v1/school/classes/:id/assign-teacher
   * @access Private (School Admin)
   */
  async assignTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { teacherId } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!teacherId) {
        res.status(400).json({
          success: false,
          message: "Teacher ID is required",
          error: "Missing teacher ID",
        } as APIResponse);
        return;
      }

      // Validate teacher - use approvalStatus instead of isApproved
      const teacher = await Teacher.findOne({
        _id: teacherId,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
        approvalStatus: "approved", // Use proper approval status
      });

      if (!teacher) {
        res.status(400).json({
          success: false,
          message: "Teacher not found or not approved",
          error: "Teacher must be approved before class assignment",
        } as APIResponse);
        return;
      }

      // Check if teacher is active
      if (teacher.status !== "active") {
        res.status(400).json({
          success: false,
          message: "Teacher is not active",
          error: "Teacher must be active to be assigned to classes",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      // Check if teacher has subject expertise for this class
      // Prefer courseIds (Course IDs) - teacher must have class's course in their courseIds
      if (classItem.courseId) {
        const teacherCourseIds = (teacher.courseIds || []).map(
          (id: mongoose.Types.ObjectId) => id.toString(),
        );
        const classCourseId = classItem.courseId.toString();
        if (
          teacherCourseIds.length > 0 &&
          !teacherCourseIds.includes(classCourseId)
        ) {
          res.status(400).json({
            success: false,
            message: "Teacher does not have expertise in this course",
            error: `Teacher needs this course in their subjects (courseIds)`,
            data: {
              requiredCourseId: classCourseId,
              teacherCourseIds,
            },
          } as APIResponse);
          return;
        }
      } else if (
        classItem.subject &&
        teacher.subjects &&
        teacher.subjects.length > 0
      ) {
        // Fallback: check legacy subjects by name
        const hasSubjectExpertise = teacher.subjects.some(
          (subject: any) =>
            subject.name.toLowerCase() === classItem.subject?.toLowerCase(),
        );
        if (!hasSubjectExpertise) {
          res.status(400).json({
            success: false,
            message: "Teacher does not have expertise in this subject",
            error: `Teacher needs expertise in ${classItem.subject}`,
            data: {
              requiredSubject: classItem.subject,
              teacherSubjects: teacher.subjects.map((s: any) => s.name),
            },
          } as APIResponse);
          return;
        }
      }

      await classItem.assignTeacher(
        mongoose.Types.ObjectId.createFromHexString(teacherId),
      );

      logger.info(`Teacher assigned to class: ${classItem.id}`, {
        teacherId,
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher assigned successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            teacherIds: classItem.teacherIds,
            removedTeacherId: teacherId,
          },
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error assigning teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to assign teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Enroll students in class
   * @route POST /api/v1/school/classes/:id/enroll-students
   * @access Private (School Admin)
   */
  async enrollStudents(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { studentIds } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (
        !studentIds ||
        !Array.isArray(studentIds) ||
        studentIds.length === 0
      ) {
        res.status(400).json({
          success: false,
          message: "Student IDs array is required",
          error: "Missing student IDs",
        } as APIResponse);
        return;
      }

      // Validate students
      const students = await Student.find({
        _id: { $in: studentIds },
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
        isApproved: true,
      });

      if (students.length !== studentIds.length) {
        res.status(400).json({
          success: false,
          message: "Some students are invalid or not approved",
          error: "Invalid students",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const validatedStudentIds = studentIds.map(id =>
        mongoose.Types.ObjectId.createFromHexString(id),
      );
      await classItem.enrollStudents(validatedStudentIds);

      logger.info(`Students enrolled in class: ${classItem.id}`, {
        studentCount: studentIds.length,
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: `${studentIds.length} students enrolled successfully`,
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            currentStudents: classItem.currentStudents,
            availableSpots: classItem.availableSpots,
          },
          enrolledStudents: students.map(student => ({
            id: student.id,
            fullName: student.fullName,
            studentId: student.studentId,
          })),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error enrolling students:", error);

      // Handle class full error
      if (error.message.includes("Cannot enroll")) {
        res.status(400).json({
          success: false,
          message: error.message,
          error: "Class capacity exceeded",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to enroll students",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all enrolled students for a class
   * @route GET /api/v1/school/classes/:id/students
   * @access Private (School Admin)
   */
  async getEnrolledStudents(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      }).populate(
        "students",
        "fullName studentId profilePicture email phone academicLevel",
      );

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: `${classItem.currentStudents} students found`,
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            currentStudents: classItem.currentStudents,
            maxStudents: classItem.maxStudents,
          },
          students: (classItem as any).students || [],
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching enrolled students:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch enrolled students",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Typeahead search for teachers (async searchable dropdown)
   * @route GET /api/v1/school/teachers/search
   * @access Private (School Admin)
   * @query search (required, min 2 chars), subject, page, limit
   * @description Designed for async typeahead/autocomplete - requires search term
   */
  async searchTeachers(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.school?.id || req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School context is required",
          error: "No school found in request context",
        } as APIResponse);
        return;
      }

      const { search, subject, page = 1, limit = 20 } = req.query;

      // Require search term for typeahead pattern
      if (!search || typeof search !== "string") {
        res.status(400).json({
          success: false,
          message: "Search term is required for typeahead",
          error: "Missing required parameter: search",
        } as APIResponse);
        return;
      }

      const searchTerm = search.trim();

      // Require minimum 2 characters for search
      if (searchTerm.length < 2) {
        res.status(400).json({
          success: false,
          message: "Search term must be at least 2 characters",
          error: "Search term too short",
        } as APIResponse);
        return;
      }

      // Build query - only approved and active teachers
      const schoolObjectId =
        mongoose.Types.ObjectId.createFromHexString(schoolId);
      const query: any = {
        schoolId: schoolObjectId,
        approvalStatus: "approved",
        status: "active",
        $or: [
          { fullName: { $regex: searchTerm, $options: "i" } },
          { employeeId: { $regex: searchTerm, $options: "i" } },
        ],
      };

      // Optional subject filter
      if (subject && typeof subject === "string") {
        query["subjects.name"] = { $regex: subject, $options: "i" };
      }

      // Pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        50,
        Math.max(10, Number.parseInt(limit as string, 10)),
      ); // Max 50 per page
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [teachers, totalCount] = await Promise.all([
        Teacher.find({ ...query })
          .select("fullName employeeId profilePicture subjects")
          .sort({ fullName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Teacher.countDocuments({ ...query }),
      ]);

      // Format for typeahead: { value, label, meta }
      const options = teachers.map(teacher => ({
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        value: String(teacher._id),
        label: `${teacher.fullName} (${teacher.employeeId})`,
        fullName: teacher.fullName,
        employeeId: teacher.employeeId,
        profileImage: teacher.profilePicture?.fileUrl ?? null,
        subjects: teacher.subjects?.map((s: { name: string }) => s.name) ?? [],
      }));

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasMore = pageNum < totalPages;

      res.status(200).json({
        success: true,
        message: `${options.length} teachers found`,
        data: {
          options,
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasMore,
            nextPage: hasMore ? pageNum + 1 : null,
          },
          searchTerm,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error searching teachers:", error);
      res.status(500).json({
        success: false,
        message: "Failed to search teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all school teachers (for assignment)
   * @route GET /api/v1/school/teachers
   * @access Private (School Admin)
   */
  async getSchoolTeachers(req: AuthRequest, res: Response): Promise<void> {
    try {
      // School context is already attached by attachSchoolContext middleware
      const schoolId = req.school?.id || req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School context is required",
          error: "No school found in request context",
        } as APIResponse);
        return;
      }

      // Extract pagination parameters
      const {
        page = 1,
        limit = 10,
        sortBy = "fullName",
        sortOrder = "asc",
        search,
        status,
        approvalStatus,
      } = req.query;

      // Build query using schoolId from middleware
      const schoolObjectId =
        mongoose.Types.ObjectId.createFromHexString(schoolId);
      const query: any = { schoolId: schoolObjectId };

      if (search) {
        query.$or = [
          { fullName: { $regex: search, $options: "i" } },
          { employeeId: { $regex: search, $options: "i" } },
        ];
      }

      if (status) {
        query.status = status;
      }

      if (approvalStatus) {
        query.approvalStatus = approvalStatus;
      }

      // Build sort object
      const sort: any = {};
      const validSortFields = [
        "fullName",
        "employeeId",
        "createdAt",
        "status",
        "approvalStatus",
      ];
      const sortField = validSortFields.includes(sortBy as string)
        ? (sortBy as string)
        : "fullName";
      sort[sortField] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      ); // Max 100 per page
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [teachers, totalCount] = await Promise.all([
        Teacher.find({ ...query })
          .select(
            "fullName employeeId profilePicture status approvalStatus createdAt bio",
          )
          .sort(sort)
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Teacher.countDocuments({ ...query }),
      ]);

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasNextPage = pageNum < totalPages;
      const hasPrevPage = pageNum > 1;

      res.status(200).json({
        success: true,
        message: `${totalCount} teachers found`,
        data: {
          teachers: teachers.map(teacher => ({
            id: teacher._id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
            profileImage: teacher.profilePicture?.fileUrl || null,
            bio: teacher.bio,
            status: teacher.status,
            approvalStatus: teacher.approvalStatus,
            createdAt: teacher.createdAt,
          })),
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasNextPage,
            hasPrevPage,
            nextPage: hasNextPage ? pageNum + 1 : null,
            prevPage: hasPrevPage ? pageNum - 1 : null,
          },
          filters: {
            search: search || null,
            status: status || null,
            approvalStatus: approvalStatus || null,
          },
          sort: {
            field: sortField,
            order: sortOrder,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school teachers:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teachers",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get specific teacher by ID for school admin
   * @route GET /api/v1/school/teachers/:teacherId
   * @access Private (School Admin)
   */
  async getSchoolTeacherById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.school?.id || req.user?.schoolId;
      const { teacherId } = req.params;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!teacherId || !mongoose.Types.ObjectId.isValid(teacherId)) {
        res.status(400).json({
          success: false,
          message: "Valid teacher ID is required",
          error: "Invalid teacher ID",
        } as APIResponse);
        return;
      }

      // Find teacher belonging to this school
      const teacher = await Teacher.findOne({
        _id: mongoose.Types.ObjectId.createFromHexString(teacherId),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
      })
        .populate("userId", "email isEmailVerified")
        .populate("schoolId", "schoolName")
        .lean();

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found or not associated with this school",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Format response
      const formattedTeacher = {
        id: teacher._id,
        employeeId: teacher.employeeId,
        fullName: teacher.fullName,
        email: (teacher.userId as any)?.email,
        phone: teacher.phone,
        profilePicture: teacher.profilePicture,
        bio: teacher.bio,
        subjects: teacher.subjects,
        education: teacher.education,
        experience: teacher.experience,
        documents: teacher.documents,
        status: teacher.status,
        approvalStatus: teacher.approvalStatus,
        isApproved: teacher.isApproved,
        hireDate: teacher.hireDate,
        terminationDate: teacher.terminationDate,
        terminationReason: teacher.terminationReason,
        approvedAt: teacher.approvedAt,
        approvedBy: teacher.approvedBy,
        rejectionReason: teacher.rejectionReason,
        school: {
          id: (teacher.schoolId as any)?._id,
          name: (teacher.schoolId as any)?.schoolName,
        },
        isEmailVerified: (teacher.userId as any)?.isEmailVerified,
        createdAt: teacher.createdAt,
        updatedAt: teacher.updatedAt,
      };

      res.status(200).json({
        success: true,
        message: "Teacher details retrieved successfully",
        data: {
          teacher: formattedTeacher,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school teacher by ID:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teacher details",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Typeahead search for students (async searchable dropdown)
   * @route GET /api/v1/school/students/search
   * @access Private (School Admin)
   * @query search (required, min 2 chars), academicLevel, academicYear, page, limit
   * @description Designed for async typeahead/autocomplete - requires search term
   */
  async searchStudents(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const {
        search,
        academicLevel,
        academicYear,
        page = 1,
        limit = 20,
      } = req.query;

      // Require search term for typeahead pattern
      if (!search || typeof search !== "string") {
        res.status(400).json({
          success: false,
          message: "Search term is required for typeahead",
          error: "Missing required parameter: search",
        } as APIResponse);
        return;
      }

      const searchTerm = search.trim();

      // Require minimum 2 characters for search
      if (searchTerm.length < 2) {
        res.status(400).json({
          success: false,
          message: "Search term must be at least 2 characters",
          error: "Search term too short",
        } as APIResponse);
        return;
      }

      // Build query - only approved and enrolled students
      const query: any = {
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        approvalStatus: "approved",
        status: "enrolled",
        $or: [
          { fullName: { $regex: searchTerm, $options: "i" } },
          { studentId: { $regex: searchTerm, $options: "i" } },
        ],
      };

      // Optional filters
      if (academicLevel && typeof academicLevel === "string") {
        query.academicLevel = academicLevel;
      }

      if (academicYear && typeof academicYear === "string") {
        query.academicYear = academicYear;
      }

      // Pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        50,
        Math.max(10, Number.parseInt(limit as string, 10)),
      ); // Max 50 per page
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [students, totalCount] = await Promise.all([
        Student.find({ ...query })
          .select(
            "fullName studentId profilePicture academicLevel academicYear",
          )
          .sort({ fullName: 1 })
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Student.countDocuments({ ...query }),
      ]);

      // Format for typeahead: { value, label, meta }
      const options = students.map(student => ({
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        value: String(student._id),
        label: `${student.fullName} (${student.studentId})`,
        fullName: student.fullName,
        studentId: student.studentId,
        profileImage: student.profilePicture?.fileUrl ?? null,
        academicLevel: student.academicLevel,
        academicYear: student.academicYear,
      }));

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasMore = pageNum < totalPages;

      res.status(200).json({
        success: true,
        message: `${options.length} students found`,
        data: {
          options,
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasMore,
            nextPage: hasMore ? pageNum + 1 : null,
          },
          searchTerm,
          filters: {
            academicLevel: academicLevel || null,
            academicYear: academicYear || null,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error searching students:", error);
      res.status(500).json({
        success: false,
        message: "Failed to search students",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all school students (for enrollment)
   * @route GET /api/v1/school/students
   * @access Private (School Admin)
   */
  async getSchoolStudents(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      console.log("Resolved schoolId -------->:", schoolId);
      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Extract pagination parameters
      const {
        page = 1,
        limit = 10,
        sortBy = "fullName",
        sortOrder = "asc",
        search,
        status,
        approvalStatus,
        academicLevel,
        academicYear,
      } = req.query;

      // Build query
      const query: any = {
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
      };
      console.log("Building query with schoolId:", schoolId);
      if (search) {
        query.$or = [
          { fullName: { $regex: search, $options: "i" } },
          { studentId: { $regex: search, $options: "i" } },
        ];
      }

      if (status) {
        query.status = status;
      }

      if (approvalStatus) {
        query.approvalStatus = approvalStatus;
      }

      if (academicLevel) {
        query.academicLevel = academicLevel;
      }

      if (academicYear) {
        query.academicYear = academicYear;
      }

      // Build sort object
      const sort: any = {};
      const validSortFields = [
        "fullName",
        "studentId",
        "createdAt",
        "status",
        "approvalStatus",
        "academicLevel",
      ];
      const sortField = validSortFields.includes(sortBy as string)
        ? (sortBy as string)
        : "fullName";
      sort[sortField] = sortOrder === "desc" ? -1 : 1;

      // Calculate pagination
      const pageNum = Math.max(1, Number.parseInt(page as string, 10));
      const limitNum = Math.min(
        100,
        Math.max(1, Number.parseInt(limit as string, 10)),
      ); // Max 100 per page
      const skip = (pageNum - 1) * limitNum;

      // Execute queries in parallel
      const [students, totalCount] = await Promise.all([
        Student.find({ ...query })
          .select(
            "fullName studentId profilePicture status approvalStatus academicLevel academicYear createdAt",
          )
          .sort(sort)
          .skip(skip)
          .limit(limitNum)
          .lean(),
        Student.countDocuments(query),
      ]);

      // Calculate pagination info
      const totalPages = Math.ceil(totalCount / limitNum);
      const hasNextPage = pageNum < totalPages;
      const hasPrevPage = pageNum > 1;

      res.status(200).json({
        success: true,
        message: `${totalCount} students found`,
        data: {
          students: students.map(student => ({
            id: student._id,
            fullName: student.fullName,
            studentId: student.studentId,
            profileImage: student.profilePicture?.fileUrl || null,
            status: student.status,
            approvalStatus: student.approvalStatus,
            academicLevel: student.academicLevel,
            academicYear: student.academicYear,
            createdAt: student.createdAt,
          })),
          pagination: {
            currentPage: pageNum,
            totalPages,
            totalCount,
            limit: limitNum,
            hasNextPage,
            hasPrevPage,
            nextPage: hasNextPage ? pageNum + 1 : null,
            prevPage: hasPrevPage ? pageNum - 1 : null,
          },
          filters: {
            search: search || null,
            status: status || null,
            approvalStatus: approvalStatus || null,
            academicLevel: academicLevel || null,
            academicYear: academicYear || null,
          },
          sort: {
            field: sortField,
            order: sortOrder,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school students:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch students",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get specific student by ID for school admin
   * @route GET /api/v1/school/students/:studentId
   * @access Private (School Admin)
   */
  async getSchoolStudentById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const { studentId } = req.params;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (!studentId || !mongoose.Types.ObjectId.isValid(studentId)) {
        res.status(400).json({
          success: false,
          message: "Valid student ID is required",
          error: "Invalid student ID",
        } as APIResponse);
        return;
      }

      // Find student belonging to this school
      const student = await Student.findOne({
        _id: mongoose.Types.ObjectId.createFromHexString(studentId),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
      })
        .populate("userId", "email isEmailVerified")
        .populate("schoolId", "schoolName")
        .lean();

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student not found or not associated with this school",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      // Format response
      const formattedStudent = {
        id: student._id,
        studentId: student.studentId,
        fullName: student.fullName,
        email: (student.userId as any)?.email,
        phone: student.phone,
        profilePicture: student.profilePicture,
        academicLevel: student.academicLevel,
        academicYear: student.academicYear,
        enrollmentDate: student.enrollmentDate,
        graduationDate: student.graduationDate,
        withdrawalDate: student.withdrawalDate,
        withdrawalReason: student.withdrawalReason,
        status: student.status,
        approvalStatus: student.approvalStatus,
        isApproved: student.isApproved,
        approvedAt: student.approvedAt,
        approvedBy: student.approvedBy,
        rejectionReason: student.rejectionReason,
        school: {
          id: (student.schoolId as any)?._id,
          name: (student.schoolId as any)?.schoolName,
        },
        isEmailVerified: (student.userId as any)?.isEmailVerified,
        createdAt: student.createdAt,
        updatedAt: student.updatedAt,
      };

      res.status(200).json({
        success: true,
        message: "Student details retrieved successfully",
        data: {
          student: formattedStudent,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching school student by ID:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch student details",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get all classes for a specific semester with teacher details
   * @route GET /api/v1/school/semesters/:semesterId/classes
   * @access Private (School Admin)
   */
  async getClassesBySemester(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { semesterId } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(semesterId)) {
        res.status(400).json({
          success: false,
          message: "Invalid semester ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Verify semester belongs to school
      const semester = await Semester.findOne({
        _id: semesterId,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!semester) {
        res.status(404).json({
          success: false,
          message: "Semester not found",
          error: "Semester not found",
        } as APIResponse);
        return;
      }

      const classes = await Class.findBySemester(
        mongoose.Types.ObjectId.createFromHexString(semesterId),
      ).populate(
        "teacher",
        "fullName employeeId profilePicture phone email subjects",
      );

      res.status(200).json({
        success: true,
        message: `${classes.length} classes found for semester`,
        data: {
          semester: {
            id: semester.id,
            semesterName: semester.semesterName,
            academicYear: semester.academicYear,
          },
          classes: classes.map(classItem => ({
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.currentStudents,
            availableSpots: classItem.availableSpots,
            classRoom: classItem.classRoom,
            teacher: (classItem as any).teacher
              ? {
                  id: (classItem as any).teacher.id,
                  fullName: (classItem as any).teacher.fullName,
                  employeeId: (classItem as any).teacher.employeeId,
                  profileImage:
                    (classItem as any).teacher.profilePicture?.fileUrl || null,
                }
              : null,
            createdAt: classItem.createdAt,
          })),
          totalCount: classes.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching classes by semester:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch classes",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get classes assigned to a specific teacher
   * @route GET /api/v1/classes/teacher/my-classes
   * @access Private (Teacher)
   */
  async getTeacherClasses(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { semesterId, status } = req.query;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      // Find teacher by userId
      const teacher = await Teacher.findOne({
        userId: mongoose.Types.ObjectId.createFromHexString(userId || ""),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
        isApproved: true,
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found or not approved",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      // Build query
      const query: any = {
        teacherId: teacher._id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId),
      };

      if (semesterId) {
        query.semesterId = mongoose.Types.ObjectId.createFromHexString(
          semesterId as string,
        );
      }

      if (status) {
        query.status = status;
      }

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const classes = await Class.find(query)
        .populate("semester", "semesterName academicYear startDate endDate")
        .populate("students", "fullName studentId profilePicture")
        .sort({ createdAt: -1 });

      res.status(200).json({
        success: true,
        message: `${classes.length} classes found`,
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
          },
          classes: classes.map(classItem => ({
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.currentStudents,
            availableSpots: classItem.availableSpots,
            enrollmentPercentage: classItem.enrollmentPercentage,
            classRoom: classItem.classRoom,
            schedule: classItem.schedule,
            semester: (classItem as any).semester,
            students: (classItem as any).students || [],
            createdAt: classItem.createdAt,
          })),
          totalCount: classes.length,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher classes:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch classes",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get specific class details for teacher
   * @route GET /api/v1/classes/teacher/my-classes/:id
   * @access Private (Teacher)
   */
  async getTeacherClassById(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: mongoose.Types.ObjectId.createFromHexString(userId || ""),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
        isApproved: true,
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found or not approved",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        teacherId: teacher._id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      })
        .populate("semester", "semesterName academicYear startDate endDate")
        .populate(
          "students",
          "fullName studentId profilePicture email phone academicLevel",
        );

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or not assigned to you",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: "Class found",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            classCode: classItem.classCode,
            description: classItem.description,
            subject: classItem.subject,
            status: classItem.status,
            isActive: classItem.isActive,
            maxStudents: classItem.maxStudents,
            currentStudents: classItem.currentStudents,
            availableSpots: classItem.availableSpots,
            enrollmentPercentage: classItem.enrollmentPercentage,
            classRoom: classItem.classRoom,
            schedule: classItem.schedule,
            settings: classItem.settings,
            materials: classItem.materials,
            semester: (classItem as any).semester,
            students: (classItem as any).students || [],
            createdAt: classItem.createdAt,
            updatedAt: classItem.updatedAt,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher class:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get students for teacher's class
   * @route GET /api/v1/classes/teacher/my-classes/:id/students
   * @access Private (Teacher)
   */
  async getTeacherClassStudents(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { id } = req.params;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Find teacher
      const teacher = await Teacher.findOne({
        userId: mongoose.Types.ObjectId.createFromHexString(userId || ""),
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
        isApproved: true,
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found or not approved",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        teacherId: teacher._id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      }).populate(
        "students",
        "fullName studentId profilePicture email phone academicLevel majorSubjectsIds minorSubjects guardians",
      );

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or not assigned to you",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      res.status(200).json({
        success: true,
        message: `${classItem.currentStudents} students found`,
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            currentStudents: classItem.currentStudents,
            maxStudents: classItem.maxStudents,
          },
          students: (classItem as any).students || [],
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher class students:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch students",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Unassign teacher from class
   * @route DELETE /api/v1/classes/:id/unassign-teacher
   * @access Private (School Admin)
   */
  async unassignTeacher(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { teacherId } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!teacherId) {
        res.status(400).json({
          success: false,
          message: "Teacher ID is required",
          error: "Missing teacher ID",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.unassignTeacher(
        mongoose.Types.ObjectId.createFromHexString(teacherId),
      );

      logger.info(`Teacher unassigned from class: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Teacher unassigned successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            teacherId: null,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error unassigning teacher:", error);
      res.status(500).json({
        success: false,
        message: "Failed to unassign teacher",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Unenroll student from class
   * @route DELETE /api/v1/classes/:id/unenroll-student/:studentId
   * @access Private (School Admin)
   */
  async unenrollStudent(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id, studentId } = req.params;
      const schoolId = req.user?.schoolId;

      if (
        !mongoose.Types.ObjectId.isValid(id) ||
        !mongoose.Types.ObjectId.isValid(studentId)
      ) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID or student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.unenrollStudent(
        mongoose.Types.ObjectId.createFromHexString(studentId),
      );

      logger.info(`Student unenrolled from class: ${classItem.id}`, {
        studentId,
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Student unenrolled successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            currentStudents: classItem.currentStudents,
            availableSpots: classItem.availableSpots,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error unenrolling student:", error);
      res.status(500).json({
        success: false,
        message: "Failed to unenroll student",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Add materials to class
   * @route POST /api/v1/classes/:id/materials
   * @access Private (School Admin, Teacher)
   */
  async addMaterials(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;
      const userId = req.user?.userId;
      const files = req.files as Express.Multer.File[];

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (!files || files.length === 0) {
        res.status(400).json({
          success: false,
          message: "No files uploaded",
          error: "Missing files",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      // Process uploaded files
      const materials = files.map(file => ({
        fileName: file.filename,
        originalName: file.originalname,
        fileSize: file.size,
        mimeType: file.mimetype,
        fileUrl: `/uploads/documents/${file.filename}`,
        uploadedAt: new Date(),
        uploadedBy: mongoose.Types.ObjectId.createFromHexString(userId || ""),
      }));

      // Add materials to class
      for (const material of materials) {
        await classItem.addMaterial(material);
      }

      logger.info(`Materials added to class: ${classItem.id}`, {
        materialCount: materials.length,
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: `${materials.length} materials added successfully`,
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
          },
          materials: materials.map(material => ({
            fileName: material.fileName,
            originalName: material.originalName,
            fileSize: material.fileSize,
            mimeType: material.mimeType,
            fileUrl: material.fileUrl,
            uploadedAt: material.uploadedAt,
          })),
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error adding materials:", error);
      res.status(500).json({
        success: false,
        message: "Failed to add materials",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Remove material from class
   * @route DELETE /api/v1/classes/:id/materials/:fileName
   * @access Private (School Admin, Teacher)
   */
  async removeMaterial(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id, fileName } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.removeMaterial(fileName);

      logger.info(`Material removed from class: ${classItem.id}`, {
        fileName,
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Material removed successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
          },
          removedFile: fileName,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error removing material:", error);
      res.status(500).json({
        success: false,
        message: "Failed to remove material",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update class schedule
   * @route PUT /api/v1/classes/:id/schedule
   * @access Private (School Admin, Teacher)
   */
  async updateSchedule(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { schedule } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.updateSchedule(schedule);

      logger.info(`Schedule updated for class: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Schedule updated successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            schedule: classItem.schedule,
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Error updating schedule:", error);

      if (error.message.includes("Schedule times must be")) {
        res.status(400).json({
          success: false,
          message: error.message,
          error: "Invalid schedule format",
        } as APIResponse);
        return;
      }

      res.status(500).json({
        success: false,
        message: "Failed to update schedule",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Update class settings
   * @route PUT /api/v1/classes/:id/settings
   * @access Private (School Admin)
   */
  async updateSettings(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const { settings } = req.body;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.updateSettings(settings);

      logger.info(`Settings updated for class: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Settings updated successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            settings: classItem.settings,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error updating settings:", error);
      res.status(500).json({
        success: false,
        message: "Failed to update settings",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Activate class
   * @route PUT /api/v1/classes/:id/activate
   * @access Private (School Admin)
   */
  async activateClass(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.activate();

      logger.info(`Class activated: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Class activated successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            status: classItem.status,
            isActive: classItem.isActive,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error activating class:", error);
      res.status(500).json({
        success: false,
        message: "Failed to activate class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Deactivate class
   * @route PUT /api/v1/classes/:id/deactivate
   * @access Private (School Admin)
   */
  async deactivateClass(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.deactivate();

      logger.info(`Class deactivated: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Class deactivated successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            status: classItem.status,
            isActive: classItem.isActive,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error deactivating class:", error);
      res.status(500).json({
        success: false,
        message: "Failed to deactivate class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Complete class
   * @route PUT /api/v1/classes/:id/complete
   * @access Private (School Admin)
   */
  async completeClass(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.complete();

      logger.info(`Class completed: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Class completed successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            status: classItem.status,
            isActive: classItem.isActive,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error completing class:", error);
      res.status(500).json({
        success: false,
        message: "Failed to complete class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Cancel class
   * @route PUT /api/v1/classes/:id/cancel
   * @access Private (School Admin)
   */
  async cancelClass(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { id } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findOne({
        _id: id,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      await classItem.cancel();

      logger.info(`Class cancelled: ${classItem.id}`, {
        className: classItem.className,
        schoolId,
      });

      res.status(200).json({
        success: true,
        message: "Class cancelled successfully",
        data: {
          class: {
            id: classItem.id,
            className: classItem.className,
            status: classItem.status,
            isActive: classItem.isActive,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error cancelling class:", error);
      res.status(500).json({
        success: false,
        message: "Failed to cancel class",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get class statistics overview
   * @route GET /api/v1/classes/statistics/overview
   * @access Private (School Admin)
   */
  async getClassStatistics(req: AuthRequest, res: Response): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const statistics = await Class.getStatistics(
        mongoose.Types.ObjectId.createFromHexString(schoolId),
      );

      res.status(200).json({
        success: true,
        message: "Class statistics retrieved successfully",
        data: {
          statistics,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching class statistics:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teacher-specific class statistics
   * @route GET /api/v1/classes/statistics/teacher/:teacherId
   * @access Private (School Admin)
   */
  async getTeacherClassStatistics(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { teacherId } = req.params;
      const schoolId = req.user?.schoolId;

      if (!mongoose.Types.ObjectId.isValid(teacherId)) {
        res.status(400).json({
          success: false,
          message: "Invalid teacher ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      // Verify teacher belongs to school
      const teacher = await Teacher.findOne({
        _id: teacherId,
        schoolId: mongoose.Types.ObjectId.createFromHexString(schoolId || ""),
        isApproved: true,
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const classes = await Class.find({
        teacherIds: mongoose.Types.ObjectId.createFromHexString(teacherId),
      });

      const statistics = {
        totalClasses: classes.length,
        activeClasses: classes.filter(
          (c: any) => c.isActive && c.status === "active",
        ).length,
        completedClasses: classes.filter((c: any) => c.status === "completed")
          .length,
        totalStudents: (() => {
          let sum = 0;
          for (const c of classes) {
            sum += c.currentStudents;
          }
          return sum;
        })(),
        averageStudentsPerClass:
          classes.length > 0
            ? Math.round(
                (() => {
                  let sum = 0;
                  for (const c of classes) {
                    sum += c.currentStudents;
                  }
                  return sum;
                })() / classes.length,
              )
            : 0,
        classesBySubject: (() => {
          const acc: Record<string, number> = {};
          for (const c of classes) {
            const subject = c.subject || "Unspecified";
            acc[subject] = (acc[subject] || 0) + 1;
          }
          return acc;
        })(),
      };

      res.status(200).json({
        success: true,
        message: "Teacher class statistics retrieved successfully",
        data: {
          teacher: {
            id: teacher.id,
            fullName: teacher.fullName,
            employeeId: teacher.employeeId,
          },
          statistics,
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Error fetching teacher class statistics:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch teacher statistics",
        error: "Internal server error",
      } as APIResponse);
    }
  }
}

// Export singleton instance
export const classController = new ClassController();
